<script setup lang="ts">
import { computed, ref } from 'vue';
import { ClipboardCheck, Droplet, Plus, Printer } from 'lucide-vue-next';
import ActionModal from '@/components/common/actionModal/ActionModal.vue';
import { Button } from '@/components/ui/button';
import { useInventoryFormModal, type InventoryFormPrefill } from '@/composables/inventory/useInventoryFormModal';
import { useInventoryScope } from '@/composables/inventory/useInventoryScope';
import { MedicineInventoryFormId } from '@/types';
import BulkCountDialog from './BulkCountDialog.vue';
import BulkPurchaseDialog from './BulkPurchaseDialog.vue';
import CountSheetDialog from './CountSheetDialog.vue';

const props = withDefaults(
    defineProps<{
        /** Opens the forms with these fields filled and locked; absent on the list and the log, where the form asks for them. */
        prefill?: InventoryFormPrefill;
    }>(),
    { prefill: () => ({}) }
);

const { initialState, stepConfig, header, loadingKey, openCreate } = useInventoryFormModal();

const isBulkCountOpen = ref(false);
const isBulkPurchaseOpen = ref(false);
const isCountSheetOpen = ref(false);

/** A detail page counts and buys its own key through the single forms; the list and the log count and buy for a whole farm at once. */
const countsOneKey = computed(() => props.prefill.drugId !== undefined);

/** Buying, spilling and counting write stock, so they need ReadWrite on the farm - or on any farm, where the form lets the reader pick one. */
const { writableFarms } = useInventoryScope();
const canWrite = computed(() => writableFarms.value.some((farm) => props.prefill.farmId === undefined || farm.farm_id === props.prefill.farmId));
</script>

<template>
    <div class="flex flex-wrap items-center gap-2">
        <!-- A sheet only reads stock, so it needs no write access. It covers every selected farm, which a single key's page has no use for. -->
        <Button v-if="!countsOneKey" variant="outline" @click="isCountSheetOpen = true">
            <Printer class="size-4" aria-hidden="true" />
            {{ $t('views.health.inventory.buttons.print') }}
        </Button>
        <ActionModal v-if="canWrite" :initial-step-index="1" :initial-state="initialState" :step-config="stepConfig" :header="header">
            <template #default="{ open }">
                <div class="flex items-center gap-x-2">
                    <Button v-if="countsOneKey" variant="outline" :disabled="loadingKey !== null" @click="openCreate(MedicineInventoryFormId.Count, prefill, open)">
                        <ClipboardCheck class="size-4" aria-hidden="true" />
                        {{ $t('views.health.inventory.buttons.count') }}
                    </Button>
                    <Button v-else variant="outline" @click="isBulkCountOpen = true">
                        <ClipboardCheck class="size-4" aria-hidden="true" />
                        {{ $t('views.health.inventory.buttons.count') }}
                    </Button>
                    <Button variant="outline" :disabled="loadingKey !== null" @click="openCreate(MedicineInventoryFormId.Waste, prefill, open)">
                        <Droplet class="size-4" aria-hidden="true" />
                        {{ $t('views.health.inventory.buttons.waste') }}
                    </Button>
                    <Button v-if="countsOneKey" :disabled="loadingKey !== null" @click="openCreate(MedicineInventoryFormId.Purchase, prefill, open)">
                        <Plus class="size-4" aria-hidden="true" />
                        {{ $t('views.health.inventory.buttons.newPurchase') }}
                    </Button>
                    <Button v-else @click="isBulkPurchaseOpen = true">
                        <Plus class="size-4" aria-hidden="true" />
                        {{ $t('views.health.inventory.buttons.newPurchase') }}
                    </Button>
                </div>
            </template>
        </ActionModal>
        <BulkCountDialog v-if="canWrite && !countsOneKey" v-model:open="isBulkCountOpen" />
        <BulkPurchaseDialog v-if="canWrite && !countsOneKey" v-model:open="isBulkPurchaseOpen" />
        <CountSheetDialog v-if="!countsOneKey" v-model:open="isCountSheetOpen" />
    </div>
</template>
